import { ComponentProps, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { ForaDatePicker } from './ForaDatePicker';

export type UseDatePickerInputReturnType = {
  /** Props to spread in the ForaDatePicker component */
  props: ComponentProps<typeof ForaDatePicker>;
  /** Self-validate  */
  validateDateInputValue: () => string[];
  /** string value YYYY-MM-DD */
  value: string;
  /** If value has changed since initialization. Used for keeping track of form changes */
  hasChangedSinceInit: () => boolean;
  /** true after the user modifies any of the inputs.  */
  isDirty: () => boolean;
};

export type UseDatePickerInputArgs = {
  /**
   * validate: important - memoize this function, to not trigger unwanted reevaluations
   * The error messages for the validation should never show, since the date picker UI is limited by the same rules as the validaton.
   * The validation are an extra precaution, in case the calendar does not work as it should, or the rules/value is changed OUTSIDE the context of the date picker.
   * It can for example occur, if a markup start date is chose FIRST, and then the employee ID of a 11 year old is filled in. The markup start date has to be after
   *  the 13th birthday, so the validation kicks in and shows the message, since the date picker now has a value otside of the allowed range.
   */
  validate?: (value: string) => string[];
  /** Inital value, use YYYY-MM-DD if inital value is set */
  initValue?: string;
  /** Lower range limit for selectable dates. Use YYYY-MM-DD */
  minDate?: string;
  /** Upper range limit for selectable dates. Use YYYY-MM-DD */
  maxDate?: string | null;
  /** What date the calendar opens up to. Default is today's date. */
  openToDate?: string;
};

export const useDatePickerInput = ({
  initValue = '',
  validate = defaultOnValidate,
  minDate,
  maxDate,
  openToDate,
}: UseDatePickerInputArgs): UseDatePickerInputReturnType => {
  const [calendarDate, setCalendarDate] = useState<Date | null>(initValue ? new Date(initValue) : null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isDirtyField, setIsDirtyField] = useState(false);

  const handleSetCalendarDate = (setCalendarFunc: SetStateAction<Date | null>) => {
    setCalendarDate(setCalendarFunc);
    setIsDirtyField(true);
  };

  useEffect(() => {
    if (initValue) {
      setCalendarDate(new Date(initValue));
    }
  }, [initValue]);

  useEffect(() => {
    if (errors.length > 0) {
      setErrors([]);
    }
    // Only trigger hook when value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarDate]);

  useEffect(() => {
    if (dateToString(calendarDate) !== initValue) {
      const errorsArr = validate(dateToString(calendarDate));
      setErrors(errorsArr);
    }
    // Validate again if validation function changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validate]);

  const validateDateInputValue = useCallback(() => {
    const errorsArr = validate(dateToString(calendarDate));
    setErrors(errorsArr);
    return errorsArr;
  }, [validate, calendarDate]);

  const hasChangedSinceInit = useCallback(() => {
    return dateToString(calendarDate) !== initValue;
  }, [initValue, calendarDate]);

  const isDirty = useCallback(() => {
    return isDirtyField;
  }, [isDirtyField]);

  const props = useMemo((): ComponentProps<typeof ForaDatePicker> => {
    const onBlur = () => {
      if (calendarDate) {
        const errorsArr = validate(dateToString(calendarDate));
        setErrors(errorsArr);
      }
    };

    const onCalendarClose = () => {
      if (calendarDate) {
        const errorsArr = validate(dateToString(calendarDate));
        setErrors(errorsArr);
      }
    };

    return {
      errors,
      minDate,
      maxDate: maxDate ?? undefined,
      openToDate,
      onBlur,
      onCalendarClose,
      calendarDate,
      setCalendarDate: handleSetCalendarDate,
    };
  }, [calendarDate, errors, maxDate, minDate, openToDate, validate]);

  const value = useMemo(() => dateToString(calendarDate), [calendarDate]);

  return {
    props,
    validateDateInputValue,
    value,
    hasChangedSinceInit,
    isDirty,
  };
};

const defaultOnValidate = () => {
  return [];
};

// When selecting a date with react-datepicker, it creates a date with time zone (client locale). This removes the time zone part and create a YYYY-MM-DD string.
const dateToString = (date: Date | null) => {
  if (!date) {
    return '';
  }
  const timezoneOffset = date.getTimezoneOffset() * 60000; //offset in milliseconds
  const withoutTimezone = new Date(date.valueOf() - timezoneOffset).toISOString().split('T')[0];
  return withoutTimezone;
};
